import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { skipToken } from '@reduxjs/toolkit/query';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useGetReferralByIdQuery } from 'store/api/referals';
import { ClassifierData } from 'types/classifiers';
import { ContactDto, PartyType } from 'types/contacts';
import { Referral, ReferralDto, ReferralForm, ReferralOtherSourceTypeCode, ReferralTypeCode } from 'types/referrals';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import PermissionButton from '../../buttons/PermissionButton';
import DecimalInput from '../../forms/inputs/rhf-inputs/DecimalInput';
import SelectInput from '../../forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from '../../forms/inputs/rhf-inputs/TextAreaInput';
import Loader from '../../Loader';
import ConfirmationModal from '../confirmation/ConfirmationModal';
import AddContactModal from '../contact/AddContactModal';
import { ModalFooterSimple } from '../ModalFooterSimple';
import { ModalHeader } from '../ModalHeader';
import { useModal } from '../useModal';
import { FORM_OVERFLOW_CUSTOM_STYLE } from '../util';

interface Props {
    titleId?: string;
    referralId?: string;
    contactId?: string;
    matterId?: string;
    classifiers: Record<string, ClassifierData>;
    onChange: (value: ReferralDto) => void;
    onClose: () => void;
    onDelete?: (referralId: string) => void;
    initialData?: ReferralForm;
}

const defaultValues: ReferralForm = {
    id: null,
    externalId: null,
    referredParty: null,
    referredMatter: null,
    referralType: null,
    employee: null,
    referralSharePct: null,
    companyParty: null,
    personParty: null,
    notes: '',
    otherSourceType: null,
    efforts: null,
};

const ReferralModal = (props: Props) => {
    const {
        titleId = 'referrals.form.edit',
        classifiers,
        referralId,
        contactId,
        matterId,
        onChange,
        onClose,
        onDelete,
        initialData,
    } = props;
    const { openModal, closeModal } = useModal();
    const [defaultFormData, setDefaultFormData] = useState<ReferralForm>(defaultValues);
    const hasDeletePermission = useHasPermissions(['DELETE.REFERRALS.ALL']);

    const { handleSubmit, control, setValue, getValues } = useForm<ReferralForm>({
        defaultValues: defaultFormData,
        values: initialData ?? defaultFormData,
    });

    const { data: referralData, isLoading: isReferralLoading } = useGetReferralByIdQuery(referralId ?? skipToken);

    useEffect(() => {
        if (initialData) {
            return;
        }

        if (referralId) {
            if (referralData) {
                const defaultData = mapReferralToReferralForm(referralData, classifiers);
                setDefaultFormData(defaultData);
            }
        } else {
            const defaultData = buildInitialAddReferralFormData(contactId, matterId);
            setDefaultFormData(defaultData);
        }
    }, [initialData, referralData, classifiers, contactId, matterId, referralId]);

    const [otherSourceType, referralType, personParty, companyParty] = useWatch({
        control,
        name: ['otherSourceType', 'referralType.value', 'personParty', 'companyParty'],
    });

    useEffect(() => {
        setValue('referralType.value', ReferralTypeCode.CONTACT);
    }, [companyParty, personParty, setValue]);

    const handleCloseAddNewContactModal = () => {
        closeModal();
    };

    const openAddNewContactModal = (): void => {
        const companyParty = getValues('companyParty');
        openModal(
            <AddContactModal
                onContactAdd={(newContact: ContactDto) =>
                    setValue('personParty', { id: newContact.id, name: newContact.name })
                }
                onClose={handleCloseAddNewContactModal}
                contactType={PartyType.PERSON}
                preSelectedEmployer={
                    companyParty
                        ? {
                              id: companyParty.id,
                              name: companyParty.name,
                              partyType: PartyType.COMPANY,
                          }
                        : undefined
                }
            />,
            handleCloseAddNewContactModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    const openAddNewCompanyContactModal = (): void => {
        openModal(
            <AddContactModal
                onContactAdd={(newContact: ContactDto) =>
                    setValue('companyParty', { id: newContact.id, name: newContact.name })
                }
                onClose={handleCloseAddNewContactModal}
                contactType={PartyType.COMPANY}
                preSelectedEmployee={undefined}
            />,
            handleCloseAddNewContactModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    const handleOnChange = (data: ReferralForm, closeModal = true) => {
        if (onChange) onChange(mapFormDataToDto(data));
        if (closeModal) {
            onClose();
        }
    };

    const handleOnDelete = () => {
        if (onDelete && referralId && !isNaN(Number(referralId))) {
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={() => {
                        onDelete(referralId);
                        closeModal();
                        onClose();
                    }}
                    content={<p className="text-body1">{t('referrals.delete-confirmation-text')}</p>}
                />
            );
        }
    };

    const onSave = async (clearFieldsAndKeepOpen = false) => {
        await handleSubmit((data) => {
            handleOnChange(data, !clearFieldsAndKeepOpen);
            if (clearFieldsAndKeepOpen) {
                setValue('companyParty', null);
                setValue('personParty', null);
                setValue('employee', null);
                setValue('referralSharePct', null);
                setValue('notes', '');
                setValue('otherSourceType', null);
                setValue('efforts', null);
                setTimeout(() => {
                    setValue('referralType', null);
                }, 0);
            }
        })();
    };

    const handleSourceChange = () => {
        setValue('otherSourceType', null);
        setValue('employee', null);
        setValue('referralSharePct', null);
        setValue('companyParty', null);
        setValue('personParty', null);
    };

    if (isReferralLoading) {
        return <Loader />;
    }

    const footerButtons = [
        <Button {...buttonStyle({ variant: 'outlined', color: 'primary' }, 'text-sm')} key="cancel" onClick={onClose}>
            {t('cancel')}
        </Button>,
        ...(!referralId
            ? [
                  <PermissionButton
                      permissions={['CREATE.REFERRALS']}
                      key="save-and-add-another"
                      color="secondary"
                      variant="contained"
                      className="text-sm"
                      onClick={() => {
                          void onSave(true);
                      }}
                  >
                      <FormattedMessage id="referrals.save-and-add-another-referral" />
                  </PermissionButton>,
              ]
            : []),
        <PermissionButton
            permissions={referralId ? ['UPDATE.REFERRALS.ALL'] : ['CREATE.REFERRALS']}
            key="add-referal"
            color="secondary"
            variant="contained"
            className="text-sm"
            onClick={() => {
                void onSave(false);
            }}
        >
            {t('save')}
        </PermissionButton>,
    ];

    if (referralId && hasDeletePermission) {
        footerButtons.splice(
            1,
            0,
            <Button
                {...buttonStyle({ variant: 'text', color: 'error' }, 'text-sm font-normal')}
                key="delete-referal"
                onClick={handleOnDelete}
            >
                {t('delete')}
            </Button>
        );
    }

    return (
        <>
            <ModalHeader title={t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-8 py-[38px] max-sm:px-4 max-sm:py-6">
                <div className="flex flex-col gap-4">
                    {defaultFormData?.id ? (
                        <InputWrapper label={'referrals.id'} name={'referral.id'} hasErrors={false} readonly={true}>
                            <>{defaultFormData?.id}</>
                        </InputWrapper>
                    ) : null}
                    {defaultFormData?.externalId ? (
                        <InputWrapper
                            label={'referrals.external-id'}
                            name={'referral.external-id'}
                            hasErrors={false}
                            readonly={true}
                        >
                            <>{defaultFormData?.externalId}</>
                        </InputWrapper>
                    ) : null}
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="referrals.form.source"
                        required
                        name="referralType"
                        options={
                            (getValidOptionsFromClassifier(classifiers['REFERRAL_TYPE']) as {
                                label: string;
                                value: string;
                            }[]) ?? []
                        }
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        placeholder="referrals.form.source"
                        onChange={handleSourceChange}
                    />

                    {referralType === ReferralTypeCode.EMPLOYEE ? (
                        <>
                            <DynamicSelectInput
                                setValue={setValue}
                                control={control}
                                label="referrals.form.employee"
                                placeholder="referrals.form.employee"
                                name="employee"
                                paginated={{
                                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                                    searchField: 'fullName',
                                }}
                                getOptionLabel="name"
                                getOptionIdentifier="id"
                                required
                            />
                            <DecimalInput
                                control={control}
                                name="referralSharePct"
                                label="referrals.form.effort"
                                placeholder="referrals.form.effort"
                                required
                            />
                        </>
                    ) : null}

                    {referralType === ReferralTypeCode.CONTACT ? (
                        <>
                            <ContactDynamicSelectPicker
                                setValue={setValue}
                                control={control}
                                label="referrals.form.company"
                                placeholder="referrals.form.company"
                                name="companyParty"
                                paginated={{
                                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                                    filters: `partyType==${PartyType.COMPANY}`,
                                    searchField: ['name', 'partyNumber'],
                                }}
                                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.COMPANY}
                                PickerModalComponent={ContactPickerModal}
                                noOptionsContent={
                                    <PermissionButton
                                        permissions={[
                                            'CREATE.PARTIES',
                                            'CREATE.PARTIES.CLIENTS',
                                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                                        ]}
                                        variant="contained"
                                        color="secondary"
                                        size="extraSmall"
                                        onClick={openAddNewCompanyContactModal}
                                    >
                                        <FormattedMessage id="contacts.add-new-contact" />
                                    </PermissionButton>
                                }
                            />
                            <ContactDynamicSelectPicker
                                setValue={setValue}
                                control={control}
                                label="referrals.form.person"
                                placeholder="referrals.form.person"
                                name="personParty"
                                paginated={{
                                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                                    filters: `partyType==${PartyType.PERSON}`,
                                    searchField: ['name', 'partyNumber'],
                                }}
                                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                                PickerModalComponent={ContactPickerModal}
                                groupSortOrder={[
                                    t('referrals.groups.company-related'),
                                    t('referrals.groups.other-people'),
                                ]}
                                noOptionsContent={
                                    <PermissionButton
                                        permissions={[
                                            'CREATE.PARTIES',
                                            'CREATE.PARTIES.CLIENTS',
                                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                                        ]}
                                        variant="contained"
                                        color="secondary"
                                        size="extraSmall"
                                        onClick={openAddNewContactModal}
                                    >
                                        <FormattedMessage id="contacts.add-new-contact" />
                                    </PermissionButton>
                                }
                            />
                        </>
                    ) : null}

                    {referralType === ReferralTypeCode.OTHER ? (
                        <SelectInput
                            setValue={setValue}
                            control={control}
                            label="referrals.form.other-type"
                            required
                            name="otherSourceType"
                            options={
                                (
                                    getValidOptionsFromClassifier(classifiers['REFERRAL_OTHER_SOURCE_TYPE']) as {
                                        label: string;
                                        value: string;
                                    }[]
                                ).sort((o1, o2) => {
                                    if (o1.value === ReferralOtherSourceTypeCode.OTHER) return 1;
                                    if (o2.value === ReferralOtherSourceTypeCode.OTHER) return -1;
                                    return o1.value.localeCompare(o2.value);
                                }) ?? []
                            }
                            getOptionLabel="label"
                            getOptionIdentifier="value"
                            placeholder="referrals.form.other-type"
                            onChange={() => {
                                setValue('companyParty', null);
                            }}
                        />
                    ) : null}
                    {otherSourceType?.value === ReferralOtherSourceTypeCode.LEGAL_DIRECTORY ||
                    otherSourceType?.value === ReferralOtherSourceTypeCode.NETWORK_OR_ALLIANCE ? (
                        <ContactDynamicSelectPicker
                            setValue={setValue}
                            control={control}
                            label="referrals.form.company"
                            placeholder="referrals.form.company"
                            name="companyParty"
                            paginated={{
                                fetch: useLazyGetMinContactsListWithFiltersQuery,
                                filters: `partyType==${PartyType.COMPANY}`,
                                searchField: ['name', 'partyNumber'],
                            }}
                            isPickerModalOptionEnabled={(option) => option.partyType === PartyType.COMPANY}
                            PickerModalComponent={ContactPickerModal}
                        />
                    ) : null}

                    <TextAreaInput
                        control={control}
                        name="notes"
                        label="referrals.form.notes"
                        placeholder="referrals.form.notes"
                        enableAutofill
                    />

                    {/** Hide the Business Development Effort selection until a better solution is found */}
                    {/* <MultiSelectInput
                        setValue={setValue}
                        control={control}
                        label="referrals.form.bd-efforts"
                        placeholder="referrals.form.bd-efforts"
                        name="efforts"
                        options={bdEffortList ?? []}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                    /> */}
                </div>
            </div>
            <ModalFooterSimple buttons={footerButtons} />
        </>
    );
};

export default ReferralModal;

const mapFormDataToDto = (form: ReferralForm): ReferralDto => ({
    id: form?.id ?? null,
    referredParty: form?.referredParty ? { id: form.referredParty.id } : null,
    referredMatter: form?.referredMatter ? { id: form.referredMatter.id } : null,
    referralTypeCode: form?.referralType?.value ?? null,
    employee: form?.employee ? { id: form.employee.id } : null,
    referralSharePct: form?.referralSharePct ?? null,
    companyParty: form?.companyParty ? { id: form.companyParty.id } : null,
    personParty: form?.personParty ? { id: form.personParty.id } : null,
    notes: form?.notes ?? null,
    otherSourceTypeCode: form?.otherSourceType?.value ?? null,
    efforts: (form?.efforts ?? []).map((value) => {
        return { id: value.id };
    }),
});

const mapReferralToReferralForm = (referral: Referral, classifiers: Record<string, ClassifierData>): ReferralForm => {
    return {
        id: referral.id,
        externalId: referral.externalId ?? null,
        referredParty: referral.referredPartyId ? { id: referral.referredPartyId } : null,
        referredMatter: referral.referredMatterId ? { id: referral.referredMatterId } : null,
        referralType: referral.referralTypeCode
            ? {
                  label: getClassifierText(classifiers['REFERRAL_TYPE'], referral.referralTypeCode),
                  value: referral.referralTypeCode,
              }
            : null,
        // Kept whole rather than reduced to {id, name} so the selected-value chip keeps the separate
        // name parts it needs to render last-name-first.
        employee: referral.employee ?? null,
        referralSharePct: referral?.referralSharePct ?? null,
        companyParty: referral?.companyParty ?? null,
        personParty: referral?.personParty ?? null,
        notes: referral.notes ?? '',
        otherSourceType: referral?.otherSourceTypeCode
            ? {
                  label: getClassifierText(classifiers['REFERRAL_OTHER_SOURCE_TYPE'], referral.otherSourceTypeCode),
                  value: referral.otherSourceTypeCode,
              }
            : null,
        efforts: referral?.efforts ?? null,
    };
};

const buildInitialAddReferralFormData = (contactId?: string, matterId?: string): ReferralForm => {
    return {
        id: null,
        externalId: null,
        referredParty: contactId ? { id: Number(contactId) } : null,
        referredMatter: matterId ? { id: Number(matterId) } : null,
        referralType: null,
        employee: null,
        referralSharePct: null,
        companyParty: null,
        personParty: null,
        notes: '',
        otherSourceType: null,
        efforts: null,
    };
};
